import type { ReactNode } from 'react'
import type { ToolInput } from '@/features/events/shared/types'
import {
  BashContextRenderer,
  EditContextRenderer,
  extractMcpToolQuery,
  GlobContextRenderer,
  GrepContextRenderer,
  McpToolContextRenderer,
  PlanApprovalContextRenderer,
  ReadContextRenderer,
  WebFetchContextRenderer,
  WebSearchContextRenderer,
  WriteContextRenderer,
} from '@/features/events/conversation/renderers'
import {
  isBashToolInput,
  isEditToolInput,
  isExitPlanModeToolInput,
  isGlobToolInput,
  isGrepToolInput,
  isReadToolInput,
  isWebFetchToolInput,
  isWebSearchToolInput,
  isWriteToolInput,
} from '@/features/events/shared/types/tool-inputs'
import { parseMcpToolName } from '@/features/events/shared/utils'

interface RendererOptions {
  readonly enableCollapse?: boolean
}

type ToolRendererMap = Record<string, (toolInput: ToolInput, options: RendererOptions) => ReactNode>

const toolRenderers: ToolRendererMap = {
  Bash: (input, options) => {
    if (!isBashToolInput(input)) return null
    return (
      <BashContextRenderer
        command={input.command}
        description={input.description}
        enableCollapse={options.enableCollapse}
      />
    )
  },

  Edit: (input) => {
    if (!isEditToolInput(input)) return null
    return (
      <EditContextRenderer
        file_path={input.file_path}
        old_string={input.old_string}
        new_string={input.new_string}
      />
    )
  },

  Write: (input) => {
    if (!isWriteToolInput(input)) return null
    return <WriteContextRenderer file_path={input.file_path} content={input.content} />
  },

  Read: (input) => {
    if (!isReadToolInput(input)) return null
    return <ReadContextRenderer file_path={input.file_path} />
  },

  Glob: (input) => {
    if (!isGlobToolInput(input)) return null
    return <GlobContextRenderer pattern={input.pattern} path={input.path} />
  },

  Grep: (input) => {
    if (!isGrepToolInput(input)) return null
    return (
      <GrepContextRenderer
        pattern={input.pattern}
        path={input.path}
        glob={input.glob}
        type={input.type}
        output_mode={input.output_mode}
        {...{ '-n': input['-n'] }}
        {...{ '-i': input['-i'] }}
        {...{ '-C': input['-C'] }}
        {...{ '-A': input['-A'] }}
        {...{ '-B': input['-B'] }}
        head_limit={input.head_limit}
        multiline={input.multiline}
      />
    )
  },

  WebFetch: (input) => {
    if (!isWebFetchToolInput(input)) return null
    return <WebFetchContextRenderer url={input.url} prompt={input.prompt} />
  },

  WebSearch: (input) => {
    if (!isWebSearchToolInput(input)) return null
    return <WebSearchContextRenderer query={input.query} />
  },
}

const defaultRendererOptions: RendererOptions = {
  enableCollapse: true,
}

export function selectToolPermissionContextRenderer(
  toolName: string | undefined,
  toolInput: ToolInput | undefined,
  options: RendererOptions = defaultRendererOptions,
): ReactNode {
  if (!toolName || !toolInput) return null

  const renderer = toolRenderers[toolName]
  if (renderer) {
    return renderer(toolInput, options)
  }

  const mcpParseResult = parseMcpToolName(toolName)
  if (mcpParseResult.isMcp) {
    const query = extractMcpToolQuery(toolInput)
    return <McpToolContextRenderer toolName={toolName} query={query} />
  }

  return <div>Unknown tool: {toolName}</div>
}

export function selectPlanApprovalContextRenderer(
  toolInput: ToolInput | undefined,
  options: RendererOptions = defaultRendererOptions,
): ReactNode {
  if (!toolInput || !isExitPlanModeToolInput(toolInput)) {
    return <PlanApprovalContextRenderer plan="" enableCollapse={options.enableCollapse} />
  }

  return (
    <PlanApprovalContextRenderer
      plan={toolInput.plan || ''}
      enableCollapse={options.enableCollapse}
    />
  )
}
